/**
 * Cloudflare Turnstile -widgetin hallinta (Sessio 7a).
 *
 * Lataa `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit`
 * dynaamisesti document.head:iin (idempotentti). Renderoi widgetin containerEl-
 * elementtiin ja palauttaa tokenin callback:in kautta.
 *
 * Dev-bypass: jos `VITE_TURNSTILE_SITE_KEY` puuttuu env:sta, hook palauttaa heti
 * "dev-bypass"-tokenin eika lataa Cloudflare-scriptia. Tama mahdollistaa local-
 * developmentin ilman Cloudflare-tilia ja yhteensopii backendin dev-bypass:n
 * kanssa.
 *
 * T9: ei npm-pakettia (`@marsidev/react-turnstile`), vaan natiivi script.
 */

import { useEffect, useRef, useState } from 'react';

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';
/** Virhekoodi kun Cloudflaren scripti ei lataudu (mainosesto, VPN, verkko). */
export const TURNSTILE_SCRIPT_LOAD_FAILED = 'script-load-failed';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          'error-callback'?: (errorCode: string) => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact';
          action?: string;
        },
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile vaatii selain-ympariston'));
  }
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(TURNSTILE_SCRIPT_LOAD_FAILED)), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => {
      scriptPromise = null;
      // Pois DOMista, jotta uudelleenyritys lisaa tuoreen scriptin eika jaa odottamaan
      // jo epaonnistuneen elementin load-tapahtumaa (existing-haara ylla).
      script.remove();
      reject(new Error(TURNSTILE_SCRIPT_LOAD_FAILED));
    });
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export type UseTurnstileOptions = {
  /** Cloudflare Turnstile -site-key. Jos undefined, palautetaan dev-bypass. */
  siteKey?: string;
  /** Kutsutaan kun Cloudflare antaa validin tokenin. */
  onToken: (token: string) => void;
  /** Kutsutaan kun token vanhenee tai widget on ladannut virheen. */
  onError?: (errorCode: string) => void;
  /** Widgetin teema (default 'light'). */
  theme?: 'light' | 'dark' | 'auto';
};

export type UseTurnstileResult = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  reset: () => void;
  /**
   * `true` jos VITE_TURNSTILE_SITE_KEY puuttuu ja dev-bypass-token annetaan
   * automaattisesti. Widget ei renderoidu naytolle.
   */
  bypassed: boolean;
  /**
   * Virhekoodi kun widget ei voi antaa tokenia (scripti ei latautunut tai Cloudflaren
   * error-callback), muuten null. Ilman tata lahetysnappi jai harmaaksi ilman selitysta
   * eika pyynto koskaan tullut palvelimelle (23.9.2026).
   */
  error: string | null;
  /** Lataa scriptin ja renderoi widgetin alusta. */
  retry: () => void;
};

/**
 * React-hook joka renderoi Cloudflare Turnstile -widgetin containerRef-elementtiin
 * ja kutsuu onToken-callbackin kun token saadaan.
 */
export function useTurnstile(opts: UseTurnstileOptions): UseTurnstileResult {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [bypassed, setBypassed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const { siteKey, onToken, onError, theme = 'light' } = opts;
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onTokenRef.current = onToken;
    onErrorRef.current = onError;
  }, [onToken, onError]);

  useEffect(() => {
    let cancelled = false;

    if (!siteKey) {
      setBypassed(true);
      onTokenRef.current('dev-bypass');
      return;
    }

    setBypassed(false);
    setError(null);
    const fail = (code: string) => {
      setError(code);
      onErrorRef.current?.(code);
    };
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        if (!window.turnstile) {
          fail(TURNSTILE_SCRIPT_LOAD_FAILED);
          return;
        }
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => {
            setError(null);
            onTokenRef.current(token);
          },
          'error-callback': (code) => fail(code || 'unknown'),
          // Vanheneminen ei ole kayttajalle nakyva virhe: widget uusii tokenin itse
          // (refresh-expired: auto). Lomakkeen token tyhjennetaan valiajaksi.
          'expired-callback': () => onErrorRef.current?.('expired'),
        });
      })
      .catch((err: Error) => {
        if (!cancelled) fail(err.message);
      });

    return () => {
      cancelled = true;
      const widgetId = widgetIdRef.current;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          // widget on jo poistettu / sivu unmounting
        }
      }
      widgetIdRef.current = null;
    };
  }, [siteKey, theme, attempt]);

  const reset = () => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  const retry = () => setAttempt((n) => n + 1);

  return { containerRef, reset, bypassed, error, retry };
}

/** Frontend-lukuinen site-key, build-aikana injektoitu Viten kautta. */
export function getTurnstileSiteKey(): string | undefined {
  const key = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  return typeof key === 'string' && key.length > 0 ? key : undefined;
}
